import { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { createSelector } from "reselect";
import Swal from "sweetalert2";

import { kiemKhoApi } from "src/1/apis/kiem_kho.api";
import { history } from "src/App";
import { useRemoveTab } from "src/common/actions/config.action";
import { store } from "src/store";

import Form from "../../../components/Form/Check";

import { DraftWatcher } from "./draft";

const selectCurrentUser = createSelector(
  (state) => state.auth,
  ({ user }) => user
);

export const DEFAULT_VALUES = {
  created_by: "",
  store_code: "",
  ware_code: "",
  checked: new Date(),
  note: "",
  group_code: "",
  value: 0,
  quantity: 1,
  materials: [
    {
      checked: false,
      code: "",
      wareUnit: "",
      wareQ: 1,
      warePrice: 0,
      total: 0,
    },
  ],
};

const InventoryCheckCreate = () => {
  //#region Data
  const user = useSelector(selectCurrentUser);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting },
  } = useForm({
    mode: "all",
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    if (user) {
      setValue("created_by", user.code);
      setValue("store_code", user.store_code);
      setValue("ware_code", user.ware_code);
    }
  }, [user]);

  const { removeTab } = useRemoveTab();

  const dispatch = useDispatch();

  const isDraftExisted = useSelector(
    (state) => state.phieuKiemKhoDraft.isDraftExisted,
    shallowEqual
  );

  const confirmUsingDraft = useCallback(async () => {
    const res = await Swal.fire({
      title: "Có 1 bản lưu nháp!",
      text: "Bạn có muốn dùng lại bản lưu nháp trước đó không?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Dùng bản lưu nháp",
      cancelButtonText: "Hủy bản nháp",
      reverseButtons: true,
      customClass: {
        confirmButton: "btn btn-success ml-3",
        cancelButton: "btn btn-danger mr-3",
      },
      allowEscapeKey: false,
      allowOutsideClick: false,
    });
    return res.isConfirmed;
  }, []);

  useEffect(() => {
    if (isDraftExisted) {
      (async () => {
        const isConfirm = await confirmUsingDraft();
        if (isConfirm) {
          const _values = store.getState().phieuKiemKhoDraft.values;
          reset(_values);
        } else {
          dispatch({ type: "CLEAR-DRAFT-PHIEU-KIEM-KHO" });
        }
      })();
    }
  }, []);
  //#endregion

  //#region Event
  const onSubmit = handleSubmit(async (values) => {
    try {
      const payload = new URLSearchParams();
      for (let [key, value] of Object.entries(values)) {
        if (key === "materials") payload.append(key, JSON.stringify(value));
        else if (key === "checked")
          payload.append(key, dayjs(value).format("YYYY-MM-DD"));
        else payload.append(key, value);
      }

      await kiemKhoApi.create(payload);
      dispatch({ type: "CLEAR-DRAFT-PHIEU-KIEM-KHO" });
      reset(DEFAULT_VALUES);
      history.push("/inventory-check/list");
      noti("success", "Tạo phiếu kiểm kho thành công!");
      removeTab("/inventory-check/form");
    } catch (error) {
      noti("error", error?.message ?? "Tạo phiếu kiểm kho không thành công!");
    }
  });
  //#endregion

  //#region Render
  return (
    <>
      <Form
        onSubmit={onSubmit}
        control={control}
        setValue={setValue}
        isLoading={isSubmitting}
      />
      <DraftWatcher control={control} />
    </>
  );
  //#endregion
};

export default InventoryCheckCreate;

//#region Lưu nháp
const initialState = {
  isDraftExisted: false,
  values: DEFAULT_VALUES,
};

const persistConfig = {
  key: "phieu-kiem-kho-draft",
  storage: storage,
};

function phieuKiemKhoDraftReducer(
  state = initialState,
  { type, payload } = { type: null, payload: null }
) {
  switch (type) {
    case "SAVE-DRAFT-PHIEU-KIEM-KHO":
      return { isDraftExisted: true, values: payload };
    case "CLEAR-DRAFT-PHIEU-KIEM-KHO":
      return { isDraftExisted: false, values: DEFAULT_VALUES };
    default:
      return state;
  }
}

export const phieuKiemKhoPersistReducer = persistReducer(
  persistConfig,
  phieuKiemKhoDraftReducer
);
//#endregion
